/**
 * Auth Split Layout
 *
 * 50/50 split layout for auth pages: form content on the left,
 * branded visual panel on the right. Right panel is hidden on mobile.
 */

import { Logo } from '@/shared/components/logo';

const AUTH_COLORS = {
  bg: '#FFFFFF',
  text: '#1B2B3A',
  textMuted: '#9BA5B0',
  accent: '#D4A853',
  accentSoft: 'rgba(212, 168, 83, 0.12)',
  border: '#E5E0D8',
  panelBg: '#1B2B3A',
  panelBgDeep: '#0F1117',
} as const;

export function AuthSplitLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen" style={{ backgroundColor: AUTH_COLORS.bg }}>
      {/* Left panel — form side */}
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        {/* Logo + branding */}
        <div className="mb-10 flex items-center gap-3">
          <Logo ignoreTheme />
          <span
            className="text-3xl"
            style={{ fontFamily: 'var(--font-dafoe)', color: AUTH_COLORS.accent }}
          >
            Tally
          </span>
        </div>

        {/* Form content */}
        <div className="w-full max-w-md">{children}</div>
      </div>

      {/* Right panel — visual side (hidden below lg) */}
      <div
        className="hidden lg:flex lg:w-1/2 lg:items-center lg:justify-center"
        style={{
          background: `linear-gradient(135deg, ${AUTH_COLORS.panelBg} 0%, ${AUTH_COLORS.panelBgDeep} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Radial gold glow */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at 50% 50%, rgba(212, 168, 83, 0.08) 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Tally mark watermark */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="300"
          height="300"
          style={{ opacity: 0.06, position: 'absolute' }}
        >
          <g
            stroke="#FFFFFF"
            strokeWidth="28"
            strokeLinecap="round"
            fill="none"
          >
            <line x1="145" y1="115" x2="145" y2="397" />
            <line x1="210" y1="115" x2="210" y2="397" />
            <line x1="275" y1="115" x2="275" y2="397" />
            <line x1="340" y1="115" x2="340" y2="397" />
            <line x1="110" y1="350" x2="375" y2="150" />
          </g>
        </svg>

        {/* "Tally" script text */}
        <span
          style={{
            fontFamily: 'var(--font-dafoe)',
            fontSize: '5rem',
            color: AUTH_COLORS.accent,
            opacity: 0.15,
            position: 'relative',
            userSelect: 'none',
          }}
        >
          Tally
        </span>
      </div>
    </div>
  );
}
